"use client";

import Image from "next/image";
import { useState } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const NAMESPACE = "new-aluminum-extrusion";
const IMAGE_BASE = "/image/new-version/more/AluminumExtrusion/order-your-aluminum-extrusion-profiles";

const PROFILE_TYPES = [
	{
		key: "custom",
		buttonKey: "profilesCustomButton",
		descKeys: ["profilesCustomDesc1", "profilesCustomDesc2", "profilesCustomDesc3"],
		typesTitleKey: "profilesCustomTypesTitle",
		itemKeys: ["profilesCustomType1", "profilesCustomType2", "profilesCustomType3", "profilesCustomType4"],
		ctaKey: "profilesCustomCta",
		images: [
			{
				src: `${IMAGE_BASE}/1/custom-aluminum-extrusions-01.webp`,
				altKey: "profilesCustomImageAlt1",
			},
			{
				src: `${IMAGE_BASE}/1/custom-aluminum-extrusions-02.webp`,
				altKey: "profilesCustomImageAlt2",
			},
			{
				src: `${IMAGE_BASE}/1/custom-aluminum-extrusions-03.webp`,
				altKey: "profilesCustomImageAlt3",
			},
		],
	},
	{
		key: "standard",
		buttonKey: "profilesStandardButton",
		descKeys: ["profilesStandardDescBefore", "profilesStandardDescBold", "profilesStandardDescAfter"],
		typesTitleKey: "profilesStandardTypesTitle",
		itemKeys: [
			"profilesStandardType1",
			"profilesStandardType2",
			"profilesStandardType3",
			"profilesStandardType4",
			"profilesStandardType5",
			"profilesStandardType6",
			"profilesStandardType7",
			"profilesStandardType8",
			"profilesStandardType9",
			"profilesStandardType10",
			"profilesStandardType11",
			"profilesStandardType12",
		],
		ctaKey: "profilesStandardCta",
		images: [
			{
				src: `${IMAGE_BASE}/2/standard-aluminum-extrusions-01.webp`,
				altKey: "profilesStandardImageAlt1",
			},
			{
				src: `${IMAGE_BASE}/2/standard-aluminum-extrusions-02.webp`,
				altKey: "profilesStandardImageAlt2",
			},
			{
				src: `${IMAGE_BASE}/2/standard-aluminum-extrusions-03.webp`,
				altKey: "profilesStandardImageAlt3",
			},
		],
	},
] as const;

type ProfileType = (typeof PROFILE_TYPES)[number];

const CheckIcon = () => (
	<span
		className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-themePrimary400 text-white"
		aria-hidden="true"
	>
		<svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M10 3L4.75 8.25L2 5.5"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	</span>
);

export default function AluminumExtrusionProfiles() {
	const t = useTranslations(NAMESPACE);
	const [activeKey, setActiveKey] = useState<ProfileType["key"]>("custom");
	const activeProfile = PROFILE_TYPES.find((profile) => profile.key === activeKey) ?? PROFILE_TYPES[0];

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("profilesTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">
						{t("profilesDescBefore")}
						<strong className="font-bold">{t("profilesDescBold")}</strong>
						{t("profilesDescAfter")}
					</p>
				</header>

				<div className="mb-7 flex flex-col items-center justify-center gap-3 sm:mb-9 sm:flex-row">
					{PROFILE_TYPES.map((profile) => {
						const isActive = profile.key === activeKey;
						return (
							<button
								key={profile.key}
								type="button"
								onClick={() => setActiveKey(profile.key)}
								className={`min-w-[230px] rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
									isActive
										? "border-themePrimary400 bg-themePrimary400 text-white shadow-sm"
										: "border-gray-300 bg-white text-black hover:border-themePrimary400 hover:text-themePrimary400"
								}`}
								aria-pressed={isActive}
							>
								{t(profile.buttonKey)}
							</button>
						);
					})}
				</div>

				<div className="grid items-start gap-8 lg:grid-cols-[0.95fr_1.2fr] lg:gap-12">
					<div className="order-2 lg:order-1">
						<div className="space-y-4 text-sm leading-relaxed text-black md:text-base">
							{activeProfile.key === "standard" ? (
								<p>
									{t("profilesStandardDescBefore")}
									<strong className="font-bold">{t("profilesStandardDescBold")}</strong>
									{t("profilesStandardDescAfter")}
								</p>
							) : (
								activeProfile.descKeys.map((descKey) => <p key={descKey}>{t(descKey)}</p>)
							)}
						</div>

						<h3 className="mb-4 mt-7 text-base font-bold text-black sm:text-lg">
							{t(activeProfile.typesTitleKey)}
						</h3>
						<ul className="space-y-3">
							{activeProfile.itemKeys.map((itemKey) => (
								<li
									key={itemKey}
									className="flex items-start gap-2.5 text-sm leading-relaxed text-black md:text-base"
								>
									<CheckIcon />
									<span>{t(itemKey)}</span>
								</li>
							))}
						</ul>

						<Link
							href="/upload"
							className="mt-7 inline-flex w-full items-center justify-center rounded bg-themePrimary400 px-5 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:w-fit sm:px-6 sm:py-3.5 md:text-base"
						>
							{t(activeProfile.ctaKey)}
						</Link>
					</div>

					<div className="order-1 grid grid-cols-2 gap-4 lg:order-2">
						{activeProfile.images.map((image, index) => (
							<div
								key={image.altKey}
								className={`relative overflow-hidden rounded-2xl border border-gray-200 bg-[#f5f7fa] ${
									index === 2 ? "col-span-2 aspect-[2.2/1]" : "aspect-[1.04/1]"
								}`}
							>
								<Image
									src={image.src}
									alt={t(image.altKey)}
									fill
									className="object-cover"
									sizes={index === 2 ? "(max-width: 1024px) 100vw, 60vw" : "(max-width: 1024px) 50vw, 30vw"}
								/>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
